import { useGridMenuItem } from 'ag-grid-react';
import { colors } from '@ardoq/design-tokens';
import { caption } from '@ardoq/typography';
import styled from 'styled-components';
import type { MenuItemDef } from 'ag-grid-enterprise';
import { APIComponentAttributes } from '@ardoq/api-types';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { Stack } from '@ardoq/layout';

type CustomColumnMenuItemProps = {
  name: string;
  description?: string;
  iconName?: IconName;
};

const Description = styled.div`
  ${caption};
  color: ${colors.textModerate};
`;

/**
 * AG Grid menu structure requirements:
 * In order for the menu to size dynamically, the default styling is provided via display: table.
 * This means that if custom menu item components are used alongside grid-provided menu items,
 * then they must adhere to a certain structure, or the grid styles must be overridden.
 *
 * The default structure consists of a parent element with display: table-row, and four children
 * with display: table-cell. If using configureDefaults and not suppressing root styling, the grid
 * will automatically add the correct styling to the parent element.
 */
const CustomColumnMenuItem = ({
  name,
  description,
  iconName,
}: CustomColumnMenuItemProps) => {
  useGridMenuItem({
    configureDefaults: () => true,
  });

  return (
    <div>
      <span
        className="ag-menu-option-part ag-menu-option-icon"
        role="presentation"
      >
        {iconName && <Icon iconName={iconName} iconSize={IconSize.SMALL} />}
      </span>
      <span className="ag-menu-option-part ag-menu-option-text">
        <Stack gap="xsmall">
          <div>{name}</div>
          {description && <Description>{description}</Description>}
        </Stack>
      </span>
      <span className="ag-menu-option-part ag-menu-option-shortcut"></span>
      <span className="ag-menu-option-part ag-menu-option-popup-pointer"></span>
    </div>
  );
};

export type ColumnMenuItemDefProps = {
  name: string;
  action: () => void;
  disabled?: boolean;
  tooltip?: string;
  description?: string;
  iconName?: IconName;
};

export const toColumnMenuItemDef = (
  props: ColumnMenuItemDefProps
): MenuItemDef<APIComponentAttributes> => {
  const menuItemParams: CustomColumnMenuItemProps = {
    name: props.name,
    description: props.description,
    iconName: props.iconName,
  };

  return {
    name: props.name,
    action: props.action,
    menuItem: CustomColumnMenuItem,
    menuItemParams,
    disabled: props.disabled,
    tooltip: props.tooltip,
  };
};
